import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { Panel } from 'primeng/panel';
import { FormsModule } from '@angular/forms';
import { BiAnalysisActionBarComponent } from './bi-analysis-action-bar/bi-analysis-action-bar.component';
import { BiAnalysisInformationComponent } from './bi-analysis-information/bi-analysis-information.component';
import { BiAnalysisService } from '../../services/bi-analysis.service';
import { ReportDefinition } from '../../model/report-definition';
import { StoredReport, StoredReportRequest } from '../../model/stored-report';
import { SmartTreeTableComponent } from './bi-analysis-table/smart-tree-table/component/smart-treetable.component';
import { BiAnalysisFlatTableComponent } from './bi-analysis-table/flat-table/component/bianalysis-flat-table.component';
import { Result } from './bi-analysis-table/flat-table/model/result';
import { ResultType } from './bi-analysis-table/flat-table/model/result-type';
import { Filter } from '../../model/filter';
import { HisStructureSharedModule } from '../../../structure/his-structure-shared.module';
import { HisNotificationService } from '../../../core/service/notifications/his-notification.service';
import { HisCoreSharedModule } from '../../../core/his-core-shared.module';
import { BiAnalysisGeneratedSqlComponent } from './bi-analysis-generated-sql/generated-sql.component';
import { HisAuthService } from '../../../auth/service/his-auth.service';

export type ReportResult =
    | { success: true; report: Result }
    | { success: false; error: string };

@Component( {
    selector: 'his-bi-analysis-result-view',
    imports: [
        HisCoreSharedModule,
        HisStructureSharedModule,
        Panel,
        FormsModule,
        OverlayPanelModule,
        BiAnalysisFlatTableComponent,
        SmartTreeTableComponent,
        BiAnalysisActionBarComponent,
        BiAnalysisInformationComponent,
        BiAnalysisGeneratedSqlComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './bi-analysis-result-view.component.html',
} )
export class BiAnalysisResultViewComponent implements OnChanges {

    @Input( { required: false } ) report: StoredReport = new StoredReport();
    @Input( { required: false } ) reportRequest: StoredReportRequest = new StoredReportRequest();

    @Input() enableReportSharing: boolean = false;
    @Output() readonly reportChanged = new EventEmitter<StoredReport>();
    @Output() readonly reportGenerated = new EventEmitter<ReportResult>();


    // This ViewChild only exists if the drill down table is shown
    @ViewChild( 'smartTreetable' ) smartTreeTable?: SmartTreeTableComponent;

    private cdr = inject( ChangeDetectorRef );
    biAnalysisService = inject( BiAnalysisService );
    notificationService = inject( HisNotificationService );
    hisAuthService = inject( HisAuthService );

    flatTableResult?: Result;
    drillDownTableResult?: Result;
    showDrillDown: boolean = true;
    tableTypeText = 'cs.bia.analysis.flatTable';
    headerOrder: string[] = [];
    totalColumn: number = 0;

    ngOnChanges( changes: SimpleChanges ): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const reportChanged = changes['report'];
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const reportRequestChanged = changes['reportRequest'];

        if ( reportRequestChanged?.currentValue ) {
            if ( reportRequestChanged.currentValue.id ) {
                this.report.id = reportRequestChanged.currentValue.id;
                this.loadStoredReport( reportRequestChanged.currentValue );
            }
            return;
        }

        if ( !reportChanged?.currentValue ) {
            return;
        }

        if ( reportChanged?.currentValue.exportedResult ) {
            this.createResultCopies( reportChanged?.currentValue.exportedResult );
            return;
        }

        if ( !reportChanged.currentValue.reportDefinition.factTableId ) {
            return;
        }

        this.generateReport( reportChanged.currentValue );
    }

    private loadStoredReport( storedReportRequest: StoredReportRequest ): void {
        this.biAnalysisService
            .getStoredReport( storedReportRequest )
            .subscribe( {
                next: ( report: StoredReport ) => {
                    let error: string = String( report.exportedResult.info?.error ?? '' );
                    if ( !error ) {
                        this.report = report;
                        this.createResultCopies( report.exportedResult );
                        this.reportGenerated.emit( {
                            success: true,
                            report: report.exportedResult
                        } );
                    } else {
                        this.reportGenerated.emit( {
                            success: false,
                            error: error
                        } );
                    }
                },
                error: ( error: unknown ) => {
                    this.reportGenerated.emit( {
                        success: false,
                        error: String( error )
                    } );
                    // nicht nötig wird scheinbar durch hisinone gesetzt...
                    // this.notificationService.error('Fehler', this.hisUi.translate('cs.bia.common.sql.error'));
                    // Optional: Fehlermeldung im UI setzen
                    // this.errorMessage = 'Der Report konnte nicht geladen werden.';
                },
                complete: () => {
                    // Optional: Wird nach erfolgreichem Abschluss aufgerufen
                }
            } );
    }

    private generateReport( report: StoredReport ): void {
        this.biAnalysisService
            .getReportData( report.reportDefinition as ReportDefinition )
            .subscribe( {
                next: ( result: Result ) => {
                    let error: string = String( result.info?.error ?? '' );
                    if ( !error ) {
                        this.createResultCopies( result );
                        this.reportGenerated.emit( {
                            success: true,
                            report: result
                        } );
                    } else {
                        this.reportGenerated.emit( {
                            success: false,
                            error: error
                        } );
                    }
                },
                error: ( error: unknown ) => {
                    this.reportGenerated.emit( {
                        success: false,
                        error: String( error )
                    } );
                    // nicht nötig wird scheinbar durch hisinone gesetzt...
                    // this.notificationService.error('Fehler', this.hisUi.translate('cs.bia.common.sql.error'));
                    // Optional: Fehlermeldung im UI setzen
                    // this.errorMessage = 'Der Report konnte nicht geladen werden.';
                },
                complete: () => {
                    // Optional: Wird nach erfolgreichem Abschluss aufgerufen
                }
            } );
    }

    private createResultCopies( result: Result ): void {
        if ( !result ) {
            this.flatTableResult = undefined;
            this.drillDownTableResult = undefined;
            return;
        }

        let jsonFlatTableResult = structuredClone( result );
        let maxAggregation: number = Result.findMax( jsonFlatTableResult.rows );
        //only not aggregated rows and totals row for flat table
        jsonFlatTableResult.rows = jsonFlatTableResult.rows.filter( r => r.aggregated === maxAggregation || r.aggregated === -1 );
        this.flatTableResult = Result.resultFactory( jsonFlatTableResult );

        this.drillDownTableResult = Result.resultFactory( structuredClone( result ) );

        // needed for download and ordering of column headers
        this.drillDownTableResult.headerOrder = this.drillDownTableResult.getMeasureColumnHeaderKeys();
        this.headerOrder = [...this.drillDownTableResult.headerOrder];
        this.flatTableResult.headerOrder = this.flatTableResult.getMeasureColumnHeaderKeys();

        this.cdr.detectChanges();
    }


    onLeftDimensionAttributesReordered( event: string[] ): void {
        const leftDimensionAttributeIds = event.filter( id =>
            this.report.reportDefinition.leftDimensionAttributeIds.includes( id )
        );

        this.generateReport( {
            ...this.report,
            reportDefinition: {
                ...this.report.reportDefinition,
                leftDimensionAttributeIds,
            },
        } );
    }

    headerOrderChanged( event: string[] ) {
        this.headerOrder = [...event];
    }

    totalColumnChanged( event: number ) {
        this.totalColumn = event;
    }

    switchTable( event: ResultType ) {
        if ( event === ResultType.DrilldownGroupable ) {
            this.showDrillDown = true;
        } else {
            this.showDrillDown = false;
        }

    }
}

